import { PropType } from 'vue';
import mixins from 'vue-typed-mixins';
import dayjs from 'dayjs';
import _ from 'lodash';

import { E勘定科目カテゴリ, E取込データ種別 } from '@/openapi/enum';
import { 勘定科目ListItemResponse, 取込Pdf試算表ListItemRequest, 取込勘定科目Request } from '@/openapi/user';

import InputableText from '@/components/inputableText/InputableText.vue';
import C勘定科目Select from '@/components/勘定科目/select/勘定科目Select.vue';
import C取込勘定科目追加Dialog from '@/components/取込/勘定科目追加/取込勘定科目追加Dialog.vue';

import { InputableTextMixin } from '@/components/inputableText/InputableText';
import 取込Mixin from '@/entries/user/mixins/取込Mixin';
import { MONTHS_OF_YEAR } from '@/classes/common/Constants';

export type Pdf試算表Tab = {
  取込データ種別: E取込データ種別;
  selectedYms: (number | undefined)[];
  list: Pdf試算表Row[];
};

export type Pdf試算表Row = {
  selected勘定科目: 勘定科目ListItemResponse | null;
  strValues: string[];
  isInMaster: boolean;
  request: 取込Pdf試算表ListItemRequest;
};

export default mixins(InputableTextMixin, 取込Mixin).extend({
  name: 'Pdf試算表Tab',

  props: {
    pdf試算表Tab: {
      type: Object as PropType<Pdf試算表Tab>,
    },
  },

  components: {
    InputableText,
    C勘定科目Select,
    C取込勘定科目追加Dialog,
  },

  data(): {
    monthsOfYear: number;
    selectedRow: Pdf試算表Row | null;
    visibleDialog: {
      科目追加: boolean;
    };
  } {
    return {
      monthsOfYear: MONTHS_OF_YEAR,
      selectedRow: null,
      visibleDialog: {
        科目追加: false,
      },
    };
  },

  computed: {
    e勘定科目カテゴリ(): E勘定科目カテゴリ {
      return this.getCカテゴリFrom取込データ種別(this.pdf試算表Tab.取込データ種別);
    },
    カテゴリ勘定科目s(): 勘定科目ListItemResponse[] {
      return this.filter勘定科目(this.pdf試算表Tab.取込データ種別);
    },
    selectedYms(): (number | undefined)[] {
      return this.pdf試算表Tab.selectedYms;
    },
    list(): Pdf試算表Row[] {
      return this.pdf試算表Tab.list;
    },
    selected取込勘定科目(): 取込勘定科目Request | undefined {
      return this.selectedRow?.request.取込勘定科目;
    },
  },

  methods: {
    change勘定科目(勘定科目ListItem: 勘定科目ListItemResponse | null, 試算表row: Pdf試算表Row): void {
      const isInMaster = 勘定科目ListItem !== null;
      試算表row.isInMaster = isInMaster;

      const request = 試算表row.request;
      request.勘定科目Pk = 勘定科目ListItem?.pk;
      if (isInMaster) request.取込勘定科目 = _.cloneDeep(勘定科目ListItem);
    },
    add勘定科目(取込勘定科目Req: 取込勘定科目Request): void {
      if (this.selectedRow === null) return;

      const name = 取込勘定科目Req.c勘定科目;
      const master = this.カテゴリ勘定科目s.find((勘) => 勘.c勘定科目 === name);

      const request = this.selectedRow.request;
      request.勘定科目Pk = master?.pk;
      request.取込勘定科目 = _.cloneDeep(master ?? 取込勘定科目Req);

      this.list
        .filter((row) => row.request.取込勘定科目!.c勘定科目 === name)
        .forEach((row) => {
          row.selected勘定科目 = null;
          row.isInMaster = true;
          row.request.勘定科目Pk = request.勘定科目Pk;
          row.request.取込勘定科目 = _.cloneDeep(request.取込勘定科目);
        });

      this.visibleDialog.科目追加 = false;
    },
    changeYms(month: number, index: number): void {
      // 未選択状態にした場合はreturn
      if (month === undefined) return;

      if (month === 0) {
        this.$_set勘定科目(index);
      } else {
        this.$_setSelectedYms(month, index);
      }
    },
    deleteColumn(index: number): void {
      this.selectedYms.splice(index, 1);
      this.list.forEach((row) => row.strValues.splice(index, 1));
    },
    changeCell(value: string | number, row: Pdf試算表Row, strValuesIndex: number): void {
      this.$set(row.strValues, strValuesIndex, String(value));
    },
    open科目追加Dialog(row: Pdf試算表Row): void {
      this.selectedRow = row;
      this.visibleDialog.科目追加 = true;
    },
    deleteRow(index: number): void {
      this.list.splice(index, 1);
    },
    $_setSelectedRow勘定科目(request: 取込Pdf試算表ListItemRequest): void {
      if (this.selectedRow === null) return;
      this.selectedRow.request = request;
    },
    $_set勘定科目(index: number): void {
      if (!confirm('勘定科目を追加しますか？')) return;

      this.list.forEach((row) => {
        const value = row.strValues[index];
        const finded = this.カテゴリ勘定科目s.find((科目) => 科目.c勘定科目 === value);
        const isInMaster = finded !== undefined;

        row.isInMaster = isInMaster;
        const request = row.request;
        request.勘定科目Pk = finded?.pk;

        if (isInMaster) {
          request.取込勘定科目 = _.cloneDeep(finded);
        } else {
          request.取込勘定科目!.c勘定科目 = value;
        }
      });
    },
    $_setSelectedYms(month: number, index: number): void {
      // 選択した列以降に順番に翌月を入れていく
      let m = dayjs().month(month - 1);
      const endMonth = m.add(1, 'year');
      for (let i = index; i < this.selectedYms.length; i++) {
        this.selectedYms[i] = m.month() + 1;
        // 翌月を設定
        m = m.add(1, 'month');
        // 月が1周した場合は終了
        if (m.isSame(endMonth)) return;
      }
    },
  },
});
